import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import _ from 'lodash';
import FullPage from 'fullpage.js';
import {getLandingSettings} from '../../api/landing.api';
import {mediaSizes} from '../../configs/media';
import LandingPageTools from './LandingPageTools';
import LandingPageAboutSection from './LandingPageAboutSection';
import LandingPagePartnersSection from './LandingPagePartnersSection';
import LandingPageReviewsSection from './LandingPageReviewsSection';
import LandingPageIntroSection from './LandingPageIntroSection';
import {LANDING_BLOCK_KEYS, SECTIONS} from '../../constants/landing';
import LandingPageVideomaterialsSection from './LandingPageVideomaterialsSection';
import LandingPageEndSection from './LandingPageEndSection';
import LandLink from './LandLink';
import LandingPageTariffsSection from './LandingPageTariffsSection';
import {NotificationError} from '../../services/notifications';
import {LanguageContext} from '../../context/LanguageContext';
import img1 from '../../assets/images/landing/img_bg_2_1.jpg';
import img2 from '../../assets/images/landing/img_bg_2_2.jpg';
import img3 from '../../assets/images/landing/img_bg_2_3.jpg';
import img3En from '../../assets/images/landing/img_bg_2_3_en.jpg';
import img4 from '../../assets/images/landing/img_bg_2_4.jpg';
import img5 from '../../assets/images/landing/img_bg_2_5.jpg';
import img6 from '../../assets/images/landing/img_bg_2_6.jpg';
import img7 from '../../assets/images/landing/img_bg_2_7.jpg';
import img8 from '../../assets/images/landing/img_bg_2_8.jpg';
import img9 from '../../assets/images/landing/img_bg_2_9.jpg';
import img10 from '../../assets/images/landing/img_bg_2_10.jpg';
import img11 from '../../assets/images/landing/img_bg_2_11.jpg';
import img12 from '../../assets/images/landing/img_bg_2_12.jpg';
import img1Md from '../../assets/images/landing/320-560/img_bg_2_1_320.png';
import img2Md from '../../assets/images/landing/320-560/img_bg_2_2_320.png';
import img3Md from '../../assets/images/landing/320-560/img_bg_2_3_320.png';
import img3MdEn from '../../assets/images/landing/320-560/img_bg_2_3_320_en.png';
import img4Md from '../../assets/images/landing/320-560/img_bg_2_4_320.png';
import img5Md from '../../assets/images/landing/320-560/img_bg_2_5_320.png';
import img6Md from '../../assets/images/landing/320-560/img_bg_2_6_320.png';
import img7Md from '../../assets/images/landing/320-560/img_bg_2_7_320.png';
import img8Md from '../../assets/images/landing/320-560/img_bg_2_8_320.png';
import img9Md from '../../assets/images/landing/320-560/img_bg_2_9_320.png';
import img10Md from '../../assets/images/landing/320-560/img_bg_2_10_320.png';
import img11Md from '../../assets/images/landing/320-560/img_bg_2_11_320.png';
import img12Md from '../../assets/images/landing/320-560/img_bg_2_12_320.png';

import 'fullpage.js/dist/fullpage.min.css';
import './LandingPage.less';

function LandingPage() {
  const language = useContext(LanguageContext);

  const fullPageRef = useRef();

  const [loading, setLoading] = useState(true);

  const [windowWidth, changeWindowWidth] = useState(window.innerWidth);

  const [windowHeight, changeWindowHeight] = useState(window.innerHeight);

  const [settings, setSettings] = useState({blocks: [], clients: []});

  const [activeSlide, changeActiveSlide] = useState('');

  const blockSettings = useMemo(() => (settings.blocks), [settings]);

  const videoMaterials = useMemo(() => (settings.videoMaterials || []), [settings]);

  const partnersSettings = useMemo(() => (settings.clients), [settings]);

  const fetchLandingSettings = useCallback(async () => {
    setLoading(true);
    await getLandingSettings().then((res) => {
      setSettings(res);
    }).catch((error) => {
      NotificationError(error.error_description || error.message);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const onWindowResize = useCallback(() => {
    const wW = window.innerWidth;
    const wH = window.innerHeight;
    changeWindowWidth(wW);
    changeWindowHeight(wH);
  }, []);

  const destroyFullPage = useCallback(() => {
    if (fullPageRef.current) {
      fullPageRef.current.destroy('all');
      fullPageRef.current = null;
    }
  }, []);

  const initFullPage = useCallback(() => {
    changeActiveSlide(LANDING_BLOCK_KEYS.INTRO);
    fullPageRef.current = new FullPage('#fullpage', {
      licenseKey: '05558DB6-5984466D-A33F10BD-AC91C099',
      onLeave: (origin, destination) => {
        changeActiveSlide(destination.anchor);
      },
    });
  }, []);

  const showSection = useMemo(() => (key) => {
    const section = _.find(settings.blocks, {key});
    return section ? section.enabled : true;
  }, [settings, loading]);

  const sectionData = useMemo(() => (key) => (_.find(settings.blocks, {key}) || {}), [settings, loading]);

  const checkFullPage = useCallback(() => {
    const wW = window.innerWidth;
    const wH = window.innerHeight;
    if (fullPageRef.current && (wW < mediaSizes.xxl || wH <= 750)) {
      destroyFullPage();
    } else if (settings.blocks.length && !fullPageRef.current && (wW > mediaSizes.xx && wH >= 750)) {
      initFullPage();
    }
  }, [settings]);

  const isMobile = useMemo(() => (windowWidth < mediaSizes.m), [windowWidth]);

  const rus = useMemo(() => (language === 'ru'), [language]);

  const getImage = useCallback((images) => {
    if (rus) {
      if (isMobile) {
        return images.ruMobile;
      }
      return images.ru;
    }
    if (isMobile) {
      return images.enMobile;
    }
    return images.en;
  }, [rus, isMobile]);

  useEffect(() => {
    void fetchLandingSettings();
    window.addEventListener('resize', onWindowResize);
    return () => {
      destroyFullPage();
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  useEffect(() => {
    checkFullPage();
  }, [windowHeight, windowWidth, settings.blocks.length]);

  return (
    <div className="index-page-wrap">
      <LandingPageTools
        activeSlide={activeSlide}
        settings={settings}
        windowWidth={windowWidth}
        loading={loading}
        language={language}
      />
      {windowWidth >= mediaSizes.xxl && windowHeight >= 850 && fullPageRef.current && (
        <LandLink
          language={language}
          sections={SECTIONS}
          blocks={blockSettings}
          type="blue"
          activeSlide={activeSlide}
        />
      )}
      <div id="fullpage">
        {showSection(LANDING_BLOCK_KEYS.INTRO) && (
          <LandingPageIntroSection
            language={language}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.ABOUT) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.ABOUT)}
            isActive={LANDING_BLOCK_KEYS.ABOUT === activeSlide}
            images={getImage({
              ru: [img1, img2, img3, img4, img5, img6, img7, img8, img9, img10, img11, img12],
              ruMobile: [img1Md, img2Md, img3Md, img4Md, img5Md, img6Md, img7Md, img8Md, img9Md, img10Md,
                img11Md, img12Md],
              en: [img1, img2, img3En, img4, img5, img6, img7, img8, img9, img10, img11, img12],
              enMobile: [img1Md, img2Md, img3MdEn, img4Md, img5Md, img6Md, img7Md, img8Md, img9Md, img10Md,
                img11Md, img12Md],
            })}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.DEMO) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.DEMO)}
            isActive={LANDING_BLOCK_KEYS.DEMO === activeSlide}
            images={isMobile ? img2Md : img2}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.INDIVIDUAL_TARIFFS) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.INDIVIDUAL_TARIFFS)}
            isActive={LANDING_BLOCK_KEYS.INDIVIDUAL_TARIFFS === activeSlide}
            images={getImage({
              ru: img3,
              ruMobile: img3Md,
              en: img3En,
              enMobile: img3MdEn,
            })}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.TARIFFS) && (
          <LandingPageTariffsSection />
        )}
        {showSection(LANDING_BLOCK_KEYS.REVIEWS) && (
          <LandingPageReviewsSection
            language={language}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.PARTNERS) && (
          <LandingPagePartnersSection
            partnersSettings={partnersSettings}
            windowWidth={windowWidth}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.VIDEO_MATERIALS) && (
          <LandingPageVideomaterialsSection videoMaterials={videoMaterials} />
        )}
        {showSection(LANDING_BLOCK_KEYS.RESUME_AND_VACANCIES) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.RESUME_AND_VACANCIES)}
            images={isMobile ? img4Md : img4}
            isActive={LANDING_BLOCK_KEYS.RESUME_AND_VACANCIES === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.VIDEOSESSIONS) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.VIDEOSESSIONS)}
            images={isMobile ? img5Md : img5}
            isActive={LANDING_BLOCK_KEYS.VIDEOSESSIONS === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.VIDEO_INTERVIEW_RECORD) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.VIDEO_INTERVIEW_RECORD)}
            images={isMobile ? img6Md : img6}
            isActive={LANDING_BLOCK_KEYS.VIDEO_INTERVIEW_RECORD === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.RESUME_AND_VACANCIES_RECORD) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.RESUME_AND_VACANCIES_RECORD)}
            images={isMobile ? img7Md : img7}
            isActive={LANDING_BLOCK_KEYS.RESUME_AND_VACANCIES_RECORD === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.VIDEO_POLL_RECORD) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.VIDEO_POLL_RECORD)}
            images={isMobile ? img8Md : img8}
            isActive={LANDING_BLOCK_KEYS.VIDEO_POLL_RECORD === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.LINK_PLACEMENT) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.LINK_PLACEMENT)}
            images={isMobile ? img9Md : img9}
            isActive={LANDING_BLOCK_KEYS.LINK_PLACEMENT === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.PORTFOLIO) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.PORTFOLIO)}
            images={isMobile ? img10Md : img10}
            isActive={LANDING_BLOCK_KEYS.PORTFOLIO === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.MARKETPLACE) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.MARKETPLACE)}
            images={isMobile ? img11Md : img11}
            isActive={LANDING_BLOCK_KEYS.MARKETPLACE === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.FEED) && (
          <LandingPageAboutSection
            language={language}
            sectionData={sectionData(LANDING_BLOCK_KEYS.FEED)}
            images={isMobile ? img12Md : img12}
            isActive={LANDING_BLOCK_KEYS.FEED === activeSlide}
          />
        )}
        {showSection(LANDING_BLOCK_KEYS.CONNECT) && (
          <LandingPageEndSection windowWidth={windowWidth} />
        )}
      </div>
    </div>
  );
}

export default LandingPage;
